import React, { useState, useMemo, useCallback } from "react";
import { ValueCell } from "../../../common/CustomCell";
import CustomStatistic from "../../../common/CustomStatistic";
import TableToolbar from "../../../common/TableToolbar";
import { DataTable } from "../../../ui/data-table";
import { cn } from "../../../ui/utils";
import { sortWithActivePriority } from "../../../../lib/sortActiveUsers";

const profitStatusColumns = [
  {
    accessorKey: "id",
    header: "아이디",
    enableSorting: true,
    size: 200,
  },
  {
    accessorKey: "userName",
    header: "이름",
    enableSorting: true,
    size: 120,
  },
  {
    accessorKey: "recommendCode",
    header: "추천인",
    enableSorting: true,
    size: 120,
  },
  {
    accessorKey: "liveProfitNLoss",
    header: "실시간손익",
    enableSorting: true,
    cell: ({ getValue }) => <ValueCell value={getValue()} />,
    size: 160,
  },
  {
    accessorKey: "totValuationGNL",
    header: "평가손익",
    enableSorting: true,
    cell: ({ getValue }) => <ValueCell value={getValue()} />,
    size: 160,
  },
  {
    accessorKey: "totalFee",
    header: "수수료",
    enableSorting: true,
    cell: ({ getValue }) => <ValueCell value={getValue()} type={1} />,
    size: 160,
  },
  {
    accessorKey: "liveUserBalance",
    header: "평가담보금",
    enableSorting: true,
    cell: ({ getValue }) => <ValueCell value={getValue()} type={0} />,
    size: 160,
  },
];

const statItems = [
  { key: "liveProfitNLoss", title: "실시간손익" },
  { key: "totValuationGNL", title: "평가손익" },
  { key: "profitNLoss", title: "실현손익" },
  { key: "totalFee", title: "수수료", type: 0 },
  { key: "liveUserBalance", title: "평가담보금", type: 2 },
];

const ProfitStatusSum = ({ data }) => {
  return (
    <div className="bg-white border px-4 py-2 mb-3 [&_[data-slot=statistic-value]]:text-lg [&_[data-slot=statistic]]:items-center">
      <span className="text-xs text-muted-foreground font-medium">
        합계 (모의투자제외)
      </span>
      <div className="flex items-center gap-0 mt-1">
        {statItems.map((item, index) => (
          <div
            key={item.key}
            className={cn(
              "flex-1 flex justify-center",
              index < statItems.length - 1 && "border-r border-border"
            )}
          >
            <CustomStatistic
              title={item.title}
              value={data[item.key]}
              type={item.type}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const PAGE_SIZE = 30;

const ProfitStatus = (props) => {
  const { data, sum } = props;

  const [searchValue, setSearchValue] = useState("");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [sorting, setSorting] = useState([{ id: "totalFee", desc: false }]);

  // 모의투자(authority=4) 제외 + 검색 필터
  const filteredData = useMemo(() => {
    const regularMembers = data.filter(
      (user) => user.authority === 0 || user.authority === 1
    );
    if (!searchValue) return regularMembers;
    return regularMembers.filter(
      (user) =>
        user.id.includes(searchValue) ||
        user.userName.includes(searchValue) ||
        user.recommendCode.includes(searchValue)
    );
  }, [data, searchValue]);

  // 전체 데이터를 먼저 정렬한 후 visibleCount만큼 슬라이스
  const dataSource = useMemo(() => {
    const sorted = sortWithActivePriority(
      filteredData,
      sorting[0]?.id,
      sorting[0]?.desc,
    );
    return sorted.slice(0, visibleCount);
  }, [filteredData, visibleCount, sorting]);

  const hasMore = visibleCount < filteredData.length;

  const handleLoadMore = useCallback(() => {
    setVisibleCount((prev) => prev + PAGE_SIZE);
  }, []);

  const handleSortingChange = useCallback((newSorting) => {
    setSorting(newSorting);
    setVisibleCount(PAGE_SIZE);
  }, []);

  const handleOnSearch = () => {
    setVisibleCount(PAGE_SIZE);
  };
  const handleReload = () => {
    setSearchValue("");
    setVisibleCount(PAGE_SIZE);
  };

  return (
    <div className="flex flex-col h-full">
      <ProfitStatusSum data={sum} />
      <TableToolbar
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onSearch={handleOnSearch}
        onReset={handleReload}
      />
      <div className="flex-1 min-h-0 overflow-hidden">
        <DataTable
          columns={profitStatusColumns}
          data={dataSource}
          getRowId={(row) => row.id}
          stickyHeader
          infiniteScroll
          hasMore={hasMore}
          onLoadMore={handleLoadMore}
          manualSorting
          initialSorting={sorting}
          onSortingChange={handleSortingChange}
        />
      </div>
    </div>
  );
};

export default React.memo(ProfitStatus);
